import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Clock, AlertTriangle, BookOpen, Scale,
  CheckCircle2, Sparkles, Mail,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/b32a9d06-cd1a-4936-8a74-34f0d65c7ad3/images/8bc51dc9b2a64cad9a38e4ae0990a6e513f718ad8cb24e83eac6fe47f2644581.jpeg";
const TEAM_IMG = "https://static.prod-images.emergentagent.com/jobs/b32a9d06-cd1a-4936-8a74-34f0d65c7ad3/images/b98feb21c6cacefe062bb540d35470abfde60e4e396714c7ca9ae495405af87a.jpeg";

const DIMENSIONI = [
  { t: "Dimensione tecnica", d: "Comprendere i principi base del funzionamento dell'IA: cos'è il machine learning, come funziona un algoritmo, perché un chatbot può produrre \"allucinazioni\"." },
  { t: "Dimensione critica", d: "Saper valutare quando fidarsi di un output dell'IA e quando no, riconoscendo i limiti intrinseci della tecnologia e l'importanza della supervisione umana." },
  { t: "Dimensione etica", d: "Comprendere le implicazioni dell'uso dell'IA su privacy, equità e diritti fondamentali, come gli obblighi di trasparenza o il rischio di discriminazione." },
  { t: "Dimensione operativa", d: "Saper utilizzare concretamente i sistemi di IA nel proprio contesto lavorativo, rispettando policy aziendali e normative, sapendo quando serve l'intervento umano." },
];

const PROGRAMMA = [
  "Cos'è l'AI literacy (art. 3, punto 56 AI Act)",
  "Chi è obbligato alla formazione AI Act: fornitori e deployer",
  "Come cambia la formazione in base al livello di rischio del sistema AI",
  "Contenuti della formazione AI literacy obbligatoria",
  "Tempistiche e scadenze AI Act 2025-2027",
  "Sanzioni e conseguenze del mancato adempimento",
  "Cosa fare subito per essere conforme: piano d'azione in 3 step",
  "L'approccio strategico alla formazione AI in azienda",
];

const ALTO_RISCHIO = [
  "Identificazione biometrica e categorizzazione delle persone",
  "Gestione di infrastrutture critiche (reti elettriche, idriche, trasporti)",
  "Istruzione e formazione professionale",
  "Occupazione e gestione lavoratori (recruiting, promozioni, licenziamenti)",
  "Accesso a servizi essenziali (credito, assicurazioni, sussidi)",
  "Applicazione della legge",
  "Migrazione e controllo frontiere",
  "Amministrazione della giustizia",
];

export default function CorsoAIActLiteracy() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Corso di alta formazione AI ACT Literacy Base — A.C.D.T."
        description="Corso di alta formazione in alfabetizzazione AI (AI ACT Literacy base): l'obbligo di AI literacy previsto dall'art. 4 del Regolamento UE 2024/1689. In arrivo."
      />
      <Navbar />

      {/* HERO */}
      <section className="relative bg-[#0a1f55] text-white overflow-hidden">
        <img src={HERO_IMG} alt="AI Act Unione Europea" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f55] via-[#0a1f55]/85 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11.5px] font-extrabold uppercase tracking-wider" data-testid="aiact-badge-in-arrivo">
            <Sparkles className="h-3.5 w-3.5" /> Corso in arrivo
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl">
            Corso di alta formazione in alfabetizzazione <span className="text-[#f59e0b]">AI ACT Literacy</span> base
          </h1>
          <p className="mt-5 text-slate-200 text-base max-w-2xl leading-relaxed">
            Dal <strong>2 febbraio 2025</strong> è entrato in vigore l'obbligo di AI literacy (alfabetizzazione) previsto
            dall'AI Act europeo — ma la maggior parte delle aziende italiane non ha ancora compreso cosa questo significhi.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="mailto:info@assoacdt.com?subject=Lista%20d%27attesa%20Corso%20AI%20ACT%20Literacy" data-testid="aiact-cta-avvisami">
              <Button className="h-12 px-6 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold">
                <Mail className="mr-2 h-4 w-4" /> Avvisami quando parte
              </Button>
            </a>
            <Link to="/corso">
              <Button variant="outline" className="h-12 px-6 border-white/40 bg-transparent text-white hover:bg-white/10 font-bold">
                Scopri il Corso A.C.D.T. attivo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Chi è obbligato */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#f59e0b]">Regolamento UE 2024/1689 — Art. 4</div>
            <h2 className="mt-2 text-base md:text-lg font-extrabold text-[#0a1f55]">Non riguarda solo chi sviluppa l'IA: riguarda anche chi la usa</h2>
            <p className="mt-4 text-slate-600 text-sm md:text-[15px] leading-relaxed">
              Non solo chi sviluppa sistemi di IA, ma anche chi utilizza strumenti come <strong>ChatGPT, Claude</strong> o
              altre soluzioni di intelligenza artificiale per le proprie attività professionali è soggetto all'obbligo di
              formazione previsto dall'<strong>articolo 4 dell'AI Act</strong>, che impone la formazione in materia di IA a
              <strong> "fornitori" e "deployer"</strong> di sistemi di intelligenza artificiale.
            </p>
            <p className="mt-3 text-slate-600 text-sm md:text-[15px] leading-relaxed">
              Tradotto: se la tua azienda sviluppa, vende o semplicemente utilizza strumenti AI per lavoro,
              <strong> devi garantire che il personale abbia competenze adeguate</strong>. L'obbligo si applica a tutti,
              dal freelance alla multinazionale, che utilizzano sistemi di IA in un contesto professionale strutturato.
            </p>
            <ul className="mt-5 space-y-2.5">
              {["Chi deve fare questa formazione", "Quali competenze sono richieste per essere conformi", "Cosa rischi se non ti adegui", "Come organizzare la formazione senza sprecare tempo e denaro"].map((x) => (
                <li key={x} className="flex items-start gap-2.5 text-[14px] text-slate-700">
                  <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
            <img src={TEAM_IMG} alt="Formazione AI in azienda" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Cos'è AI literacy */}
      <section className="bg-[#f5f7ff] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#f59e0b]">Cos'è l'AI literacy</div>
            <h2 className="mt-2 text-base md:text-lg font-extrabold text-[#0a1f55]">Non è semplicemente "saper usare l'intelligenza artificiale"</h2>
            <blockquote className="mt-5 rounded-2xl bg-white border-l-4 border-[#f59e0b] p-5 text-slate-600 text-sm md:text-[14.5px] italic leading-relaxed">
              "Le competenze, le conoscenze e la comprensione che consentono ai fornitori, ai deployer e alle persone
              interessate […] di procedere a una diffusione informata dei sistemi di IA, nonché di acquisire consapevolezza
              in merito alle opportunità e ai rischi dell'IA e ai possibili danni che essa può causare."
              <div className="mt-2 not-italic text-[12px] font-bold text-[#0a1f55]">— AI Act, articolo 3, punto 56</div>
            </blockquote>
            <p className="mt-4 text-slate-600 text-sm md:text-[15px] leading-relaxed">
              Il requisito legale non è l'erogazione di un corso, ma il raggiungimento di un <strong>risultato dimostrabile</strong>:
              consentire una "diffusione informata" e creare "consapevolezza". Un approccio che ricalca il principio di
              <strong> accountability</strong> del GDPR: l'organizzazione deve rispettare le norme <em>e</em> saper dimostrare la propria conformità.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DIMENSIONI.map((d, i) => (
              <div key={d.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-lg hover:border-[#102a6b]/30 transition-all">
                <div className="h-9 w-9 rounded-xl bg-[#0a1f55] text-[#f59e0b] font-extrabold flex items-center justify-center">{i + 1}</div>
                <h3 className="mt-3 font-extrabold text-[#0a1f55] text-[14.5px]">{d.t}</h3>
                <p className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">{d.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rischio + programma */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-10">
        <div className="rounded-3xl border border-slate-200 p-7">
          <div className="flex items-center gap-2 text-[#0a1f55] font-extrabold text-base md:text-lg"><Scale className="h-5 w-5 text-[#f59e0b]" /> Formazione calibrata sul rischio</div>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">
            L'AI Act distingue tra sistemi a rischio <strong>minimo, limitato e alto</strong>: più è alto il rischio, più
            approfondita deve essere la formazione. Chi usa un filtro antispam non ha gli stessi obblighi di chi gestisce
            recruiting automatizzato. Le 8 categorie ad alto rischio (Allegato III):
          </p>
          <ul className="mt-4 space-y-2">
            {ALTO_RISCHIO.map((x) => (
              <li key={x} className="flex items-start gap-2 text-[13px] text-slate-700"><AlertTriangle className="h-4 w-4 text-[#f59e0b] shrink-0 mt-0.5" /> {x}</li>
            ))}
          </ul>
          <p className="mt-4 text-[13px] text-slate-500 leading-relaxed">
            Per i sistemi ad alto rischio il personale deve comprendere gestione del rischio (Art. 9), sorveglianza umana
            qualificata (Art. 14) e valutazione d'impatto sui diritti fondamentali (Art. 27).
          </p>
        </div>
        <div className="rounded-3xl bg-[#0a1f55] text-white p-7">
          <div className="flex items-center gap-2 font-extrabold text-base md:text-lg"><BookOpen className="h-5 w-5 text-[#f59e0b]" /> Programma del corso</div>
          <ul className="mt-5 space-y-3">
            {PROGRAMMA.map((x, i) => (
              <li key={x} className="flex items-start gap-3 text-[13.5px] text-slate-200">
                <span className="h-6 w-6 shrink-0 rounded-lg bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold flex items-center justify-center">{i + 1}</span>
                {x}
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-2xl bg-white/10 p-4 text-[13px] flex items-start gap-2.5">
            <ShieldCheck className="h-5 w-5 text-[#f59e0b] shrink-0" />
            <span>Percorso in coerenza con l'obbligo formativo dell'art. 4 AI Act, con attestazione di partecipazione tracciata dalla piattaforma A.C.D.T.</span>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white py-16">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f59e0b]/20 border border-[#f59e0b]/40 text-[#fbbf24] text-[11.5px] font-extrabold uppercase tracking-wider">
            <Clock className="h-3.5 w-3.5" /> In arrivo — apertura iscrizioni a breve
          </div>
          <h2 className="mt-4 text-base md:text-lg font-extrabold">Vuoi essere tra i primi a partecipare?</h2>
          <p className="mt-2 text-slate-300 text-sm">Scrivici per entrare in lista d'attesa: ti avviseremo appena il corso sarà disponibile.</p>
          <a href="mailto:info@assoacdt.com?subject=Lista%20d%27attesa%20Corso%20AI%20ACT%20Literacy" className="inline-block mt-6" data-testid="aiact-cta-footer">
            <Button className="h-12 px-8 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold">
              <Mail className="mr-2 h-4 w-4" /> Entra in lista d'attesa
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
